import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchGroupRows } from '../api/endpointsBackend';

/**
 * MI-336 — the ungrouped Movements list, windowed by rows.
 *
 * It used to page by *dates*: 31 distinct dates with data per request. That
 * unit cannot carry an ordering. "By amount" over the 31 most recent dates is
 * a ranking of whatever happened to be loaded, so the largest movement of the
 * period arrives from below on the next "load more" and jumps to the top — an
 * order that rearranges what the reader already read.
 *
 * So the list reads through the same row-window endpoint the grouped view
 * uses, with **no scope**: the ungrouped list is a group with no predicate.
 * The server orders the whole filtered set in SQL and returns a window plus an
 * opaque cursor; nothing is ever sorted on the client, which is what makes the
 * order independent of what is in memory.
 */

/** One window. Large enough that "load more" is rare, small enough that the
 *  first paint is not waiting on the whole period. */
export const ROWS_PER_FETCH = 50;

/** The server's ceiling for one window (GroupRowsSchema.limit). A refetch of
 *  everything on screen is clamped to it. */
const MAX_WINDOW = 500;

export function useMovementsData({
  dateRange,
  filters = null,
  filterGroups = [],
  groupsOperator = 'and',
  ordering = 'date_desc',
  displayCurrency = null,
  enabled = true,
} = {}) {
  const [movementsList, setMovementsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [total, setTotal] = useState(0);

  const abortControllerRef = useRef(null);

  // Serialized for stable dependency comparison
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);
  const groupsKey = useMemo(() => JSON.stringify(filterGroups), [filterGroups]);
  const dateRangeKey = useMemo(() => JSON.stringify(dateRange), [dateRange]);

  const paginationInfo = useMemo(() => ({
    // The whole filtered range, not this window — what "showing 50 of 2,419"
    // has to count. Read once, on the first window of each query.
    totalInRange: total,
  }), [total]);

  /**
   * Fetch one window. `append` is what turns paging into loading more: the
   * next window added to what is on screen instead of replacing it.
   *
   * `limit` overrides the window size for a refetch — re-reading everything
   * the reader has scrolled to, so an edit on the fourth window does not drop
   * them back to the first. `quiet` keeps the rows on screen while it reads,
   * instead of swapping the list for a spinner.
   */
  const fetchWindow = useCallback(async (nextCursor, { append = false, limit, quiet = false } = {}) => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (!append && !quiet) setLoading(true);
    setError(null);

    try {
      const data = await fetchGroupRows({
        filters: filters || [],
        groups: filterGroups,
        groupsOperator,
        // No scope: the whole filtered set.
        scopes: [],
        orderBy: ordering,
        limit: Math.min(limit || ROWS_PER_FETCH, MAX_WINDOW),
        cursor: nextCursor,
        start_date: dateRange?.start_date || null,
        end_date: dateRange?.end_date || null,
        // MI-318 — so an amount ordering ranks by converted value rather than
        // by digit count across currencies.
        displayCurrency,
        // Only the first window pays for the COUNT.
        withTotal: !nextCursor,
        signal: controller.signal,
      });

      if (controller.signal.aborted) return;

      setMovementsList(prev => (append ? [...prev, ...(data.items || [])] : (data.items || [])));
      setCursor(data.next || null);
      if (data.total != null) setTotal(data.total);
      setLoading(false);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      console.error('Failed to fetch movements:', err);
      setError('Failed to fetch movements. Please try again.');
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRangeKey, filtersKey, groupsKey, groupsOperator, ordering,
      displayCurrency, enabled]);

  /** Append the next window. */
  const loadMore = useCallback(() => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    fetchWindow(cursor, { append: true }).finally(() => setLoadingMore(false));
  }, [cursor, loadingMore, fetchWindow]);

  /**
   * Re-read what is on screen after something changed underneath it — one
   * request for the whole loaded span rather than replaying each window.
   */
  const rowsLoaded = movementsList.length;
  const refetch = useCallback(
    ({ quiet = false } = {}) => fetchWindow(null, { limit: Math.max(rowsLoaded, ROWS_PER_FETCH), quiet }),
    [fetchWindow, rowsLoaded],
  );

  // First window whenever the query changes (filters, range, ordering, unit).
  useEffect(() => {
    setCursor(null);
    setTotal(0);
    fetchWindow(null);
    return () => abortControllerRef.current?.abort();
  }, [fetchWindow]);

  // Patch specific movements locally without refetching
  const patchMovements = useCallback((movementIds, patch) => {
    const idSet = new Set(movementIds);
    setMovementsList(prev =>
      prev.map(m => idSet.has(m.id) ? { ...m, ...patch } : m)
    );
  }, []);

  return {
    movementsList,
    loading,
    loadingMore,
    error,
    paginationInfo,
    hasMore: Boolean(cursor),
    loadMore,
    // How many rows are on screen — what an export of "what I am looking at"
    // has to cover now that the unit is rows.
    rowsLoaded,
    refetch,
    patchMovements,
  };
}
